import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { Loader2, MapPin, Building2, CheckCircle2, Briefcase, Upload, FileText, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const mediaUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

const TYPES = ["All", "Full-time", "Internship", "Remote"];
const TYPE_TKEYS = {
  All: "jobs.filterAll",
  "Full-time": "jobs.filterFullTime",
  Internship: "jobs.filterInternship",
  Remote: "jobs.filterRemote",
};

const typeColor = (t) =>
  ({ "Full-time": "bg-amber-600", Internship: "bg-amber-600", Remote: "bg-emerald-600" }[t] || "bg-slate-600");

export default function Jobs() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState(null);
  const [type, setType] = useState("All");
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cover, setCover] = useState("");
  const [resume, setResume] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const resumeRef = useRef(null);

  const load = async (t = type) => {
    const { data } = await api.get("/jobs", { params: { type: t } });
    setJobs(data);
    setSelected((prev) => data.find((j) => j.job_id === prev?.job_id) || data[0] || null);
  };
  useEffect(() => {
    load("All");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeType = (t) => {
    setType(t);
    load(t);
  };

  const submitApplication = async () => {
    setBusy(true);
    try {
      let resume_path = "";
      let resume_filename = "";
      if (resumeFile) {
        const fd = new FormData();
        fd.append("file", resumeFile);
        const { data } = await api.post("/upload/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
        resume_path = data.resume_path;
        resume_filename = data.resume_filename;
      }
      await api.post(`/jobs/${selected.job_id}/apply`, { cover_note: cover, resume_link: resume, resume_path, resume_filename });
      toast.success(t("jobs.applySuccess"));
      setDialogOpen(false);
      setCover("");
      setResume("");
      setResumeFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("jobs.applyError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-10" data-testid="job-board">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2">
          {t("jobs.title")}
        </h1>
        <p className="text-slate-500 mb-6">{t("jobs.subtitle")}</p>

        <div className="flex flex-wrap gap-2 mb-8">
          {TYPES.map((ty) => (
            <button
              key={ty}
              data-testid={`job-filter-${ty.toLowerCase()}`}
              onClick={() => changeType(ty)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                type === ty ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
              }`}
            >
              {t(TYPE_TKEYS[ty])}
            </button>
          ))}
        </div>

        {jobs === null ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
          </div>
        ) : jobs.length === 0 ? (
          <p className="text-center text-slate-400 py-16">{t("jobs.noJobs")}</p>
        ) : (
          <div className="grid lg:grid-cols-[380px_1fr] gap-6">
            {/* List */}
            <div className="space-y-3" data-testid="job-list">
              {jobs.map((j) => (
                <Card
                  key={j.job_id}
                  data-testid="job-card"
                  onClick={() => setSelected(j)}
                  className={`cursor-pointer border p-4 transition-all hover:shadow-md ${
                    selected?.job_id === j.job_id ? "border-amber-500 ring-1 ring-amber-500" : "border-slate-200"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3 min-w-0">
                      {j.company_logo && (
                        <img src={mediaUrl(j.company_logo)} alt="" className="h-10 w-10 shrink-0 rounded-lg border border-slate-200 object-contain bg-white" data-testid="job-logo" />
                      )}
                      <div className="min-w-0">
                        <h3 className="font-heading font-bold text-slate-900">{j.title}</h3>
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                          <Building2 className="h-3.5 w-3.5" /> {j.company}
                        </p>
                      </div>
                    </div>
                    <Badge className={`${typeColor(j.type)} shrink-0`}>{j.type}</Badge>
                  </div>
                  <p className="mt-2 text-xs text-slate-400 flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" /> {j.location}
                  </p>
                  {j.applied && (
                    <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {t("jobs.applied")}
                    </span>
                  )}
                </Card>
              ))}
            </div>

            {/* Detail */}
            {selected && (
              <Card className="lg:sticky lg:top-24 h-fit border-slate-200 p-8" data-testid="job-detail">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0">
                    {selected.company_logo && (
                      <img src={mediaUrl(selected.company_logo)} alt="" className="h-14 w-14 shrink-0 rounded-xl border border-slate-200 object-contain bg-white" data-testid="job-detail-logo" />
                    )}
                    <div className="min-w-0">
                      <h2 className="font-heading text-2xl font-extrabold text-slate-900">{selected.title}</h2>
                      <p className="mt-1 text-slate-600 flex flex-wrap items-center gap-2">
                        <Building2 className="h-4 w-4" /> {selected.company}
                        <span className="text-slate-300">•</span>
                        <MapPin className="h-4 w-4" /> {selected.location}
                      </p>
                    </div>
                  </div>
                  <Badge className={typeColor(selected.type)}>{selected.type}</Badge>
                </div>

                {selected.salary && (
                  <p className="mt-4 inline-block rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700">
                    {selected.salary}
                  </p>
                )}

                <p className="mt-6 text-slate-600 leading-relaxed">{selected.description}</p>

                {selected.requirements?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">
                      {t("jobs.requirements")}
                    </h4>
                    <ul className="space-y-2">
                      {selected.requirements.map((r) => (
                        <li key={r} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" /> {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-8">
                  {selected.applied ? (
                    <Button disabled className="w-full bg-emerald-600" data-testid="job-applied-button">
                      <CheckCircle2 className="mr-2 h-4 w-4" /> {t("jobs.applicationSubmitted")}
                    </Button>
                  ) : (
                    <Button
                      className="w-full bg-amber-600 hover:bg-amber-700"
                      onClick={() => setDialogOpen(true)}
                      data-testid="job-apply-open"
                    >
                      <Briefcase className="mr-2 h-4 w-4" /> {t("jobs.applyNow")}
                    </Button>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="apply-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("jobs.applyTo", { title: selected?.title })}</DialogTitle>
            <DialogDescription>{t("jobs.submitDetailsTo", { company: selected?.company })}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("jobs.resumeFileLabel")}</Label>
              <input
                ref={resumeRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="apply-resume-file"
              />
              {resumeFile ? (
                <div className="mt-1.5 flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="resume-file-chip">
                  <span className="flex items-center gap-2 text-sm text-slate-700 truncate">
                    <FileText className="h-4 w-4 shrink-0 text-amber-600" /> {resumeFile.name}
                  </span>
                  <button type="button" onClick={() => setResumeFile(null)} className="text-slate-400 hover:text-slate-700" data-testid="remove-resume-file">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <Button type="button" variant="outline" onClick={() => resumeRef.current?.click()} className="mt-1.5 w-full" data-testid="apply-resume-upload-btn">
                  <Upload className="mr-2 h-4 w-4" /> {t("jobs.uploadResume")}
                </Button>
              )}
              <p className="mt-1 text-xs text-slate-400">{t("jobs.resumeFileHint")}</p>
            </div>
            <div>
              <Label htmlFor="resume">{t("jobs.resumeLinkLabel")}</Label>
              <Input
                id="resume"
                data-testid="apply-resume-input"
                value={resume}
                onChange={(e) => setResume(e.target.value)}
                placeholder="https://…"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="cover">{t("jobs.coverLabel")}</Label>
              <Textarea
                id="cover"
                data-testid="apply-cover-input"
                value={cover}
                onChange={(e) => setCover(e.target.value)}
                placeholder={t("jobs.coverPlaceholder")}
                rows={4}
                className="mt-1.5"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button
              onClick={submitApplication}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="job-apply-submit"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {t("jobs.submitApplication")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
